// Public demo-access request form. Submitted to POST /v1/demo/access-requests;
// the operator decides from the admin /demo-requests page and an approval
// emails a single-use timed token. We deliberately do not show the
// requester their request id back — they should be tracking via the email
// they entered, not a UUID.

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, demo } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { dirFor } from '@/i18n';

export const Route = createFileRoute('/demo/request')({
  component: DemoRequestPage,
});

function DemoRequestPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [useCase, setUseCase] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = dirFor(i18n.language);
  }, [i18n.language]);

  // If the install isn't in demo mode, kick the visitor back to login —
  // there's nothing on this page for them.
  const info = useQuery({
    queryKey: ['demo-info'],
    queryFn: demo.info,
    retry: false,
  });
  useEffect(() => {
    if (info.isFetched && !info.data) navigate({ to: '/login' });
    if (info.data && info.data.mode !== 'request_access') navigate({ to: '/login' });
  }, [info.isFetched, info.data, navigate]);

  const submit = useMutation({
    mutationFn: () =>
      demo.submitRequest({
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || null,
        phone: phone.trim() || null,
        use_case: useCase.trim() || null,
      }),
    onSuccess: () => setSubmitted(true),
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : (t('demo.request.error') as string),
      ),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    submit.mutate();
  };

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-md shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" />
          <div className="space-y-1">
            <CardTitle className="text-xl">
              {submitted ? t('demo.request.thanks_title') : t('demo.request.title')}
            </CardTitle>
            <CardDescription>
              {submitted
                ? t('demo.request.thanks_subtitle')
                : t('demo.request.subtitle')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">{t('demo.request.thanks_body')}</p>
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline">
                  <Link to="/demo/login">{t('demo.request.have_token_cta')}</Link>
                </Button>
                <Button asChild size="sm" variant="ghost">
                  <Link to="/login">{t('common.back')}</Link>
                </Button>
              </div>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={onSubmit}>
              <Field
                id="name"
                label={t('demo.request.name_label')}
                value={name}
                onChange={setName}
                autoComplete="name"
                required
                autoFocus
              />
              <Field
                id="email"
                type="email"
                label={t('demo.request.email_label')}
                value={email}
                onChange={setEmail}
                autoComplete="email"
                required
              />
              <Field
                id="company"
                label={t('demo.request.company_label')}
                value={company}
                onChange={setCompany}
                autoComplete="organization"
              />
              <Field
                id="phone"
                label={t('demo.request.phone_label')}
                value={phone}
                onChange={setPhone}
                autoComplete="tel"
              />
              <div className="space-y-1.5">
                <Label htmlFor="use_case">{t('demo.request.use_case_label')}</Label>
                <textarea
                  id="use_case"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  maxLength={2000}
                  placeholder={t('demo.request.use_case_placeholder') as string}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={submit.isPending || !name.trim() || !email.trim()}
              >
                {submit.isPending ? t('common.saving') : t('demo.request.submit')}
              </Button>
              <Button asChild type="button" variant="ghost" className="w-full">
                <Link to="/login">{t('common.back')}</Link>
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required,
  autoFocus,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
